import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { CS } from "@/constants/testIds/clickshield";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#040914] bg-tech-grid flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute -top-32 right-0 w-[30rem] h-[30rem] rounded-full bg-cyan-neon/10 blur-[130px] pointer-events-none" />
      <Link to="/login" className="absolute top-6 left-6 inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to login
      </Link>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <ShieldCheck className="w-8 h-8 text-cyan-neon" />
          <span className="font-display text-xl text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
        </div>
        <div className="rounded-2xl neon-border bg-[#0B101A]/80 backdrop-blur-xl p-8">
          {sent ? (
            <div data-testid={CS.forgotSuccess} className="text-center py-4">
              <MailCheck className="w-10 h-10 text-[#39FF14] mx-auto mb-4" />
              <h1 className="font-display text-2xl text-white mb-2">Check your inbox</h1>
              <p className="text-sm text-slate-400 leading-relaxed">
                If <span className="text-white">{email}</span> is registered, a reset link is on its way. It expires in 1 hour.
              </p>
              <Link to="/login" className="inline-block mt-6 text-sm text-cyan-neon hover:underline">Back to login</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl text-white mb-1.5">Forgot your password?</h1>
              <p className="text-sm text-slate-400 mb-7">Enter your email and we'll send you a secure reset link.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="font-mono text-[11px] tracking-widest uppercase text-slate-500">Email</label>
                  <input
                    data-testid={CS.forgotEmail}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@company.com"
                    className="mt-2 w-full px-4 py-3 rounded-lg bg-[#040914] border border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-neon/50 focus:outline-none transition-colors"
                  />
                </div>
                {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
                <button
                  data-testid={CS.forgotSubmit}
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:glow-cyan transition-shadow inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
